import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../../../lib/supabaseAdmin';
import { Plus, CreditCard as Edit, Trash2, Search, Save, X, Check, AlertCircle, Home, Car, Store, CreditCard as CreditCardIcon, Wallet, Banknote as BanknoteIcon, Building2, ArrowUpDown, Eye, EyeOff } from 'lucide-react';

interface FeaturedLoan {
  id: string;
  bank_id: string;
  bank_name: string;
  bank_logo: string;
  product_name: string;
  loan_type: string;
  description: string;
  interest_rate: string;
  min_amount: string;
  max_amount: string;
  min_term: string;
  max_term: string;
  features: string[];
  campaign_title?: string;
  campaign_description?: string;
  campaign_end_date?: string;
  redirect_url: string;
  display_order: number;
  is_featured: boolean;
  is_active: boolean;
}

interface FeaturedCard {
  id: string;
  bank_id: string;
  bank_name: string;
  bank_logo: string;
  card_name: string;
  description: string;
  annual_fee: string;
  cashback?: string;
  welcome_bonus?: string;
  additional_card_fee?: string;
  features: string[];
  benefits: string[];
  campaign_title?: string;
  campaign_description?: string;
  redirect_url: string;
  display_order: number;
  is_featured: boolean;
  is_active: boolean;
}

interface FeaturedDeposit {
  id: string;
  bank_id: string;
  bank_name: string;
  bank_logo: string;
  product_name: string;
  deposit_type: string;
  description: string;
  interest_rate: string;
  min_amount: string;
  min_term: string;
  features: string[];
  campaign_title?: string;
  campaign_description?: string;
  redirect_url: string;
  display_order: number;
  is_featured: boolean;
  is_active: boolean;
}

const ProductManagement = () => {
  const [activeTab, setActiveTab] = useState<'loans' | 'cards' | 'deposits'>('loans');
  const [loans, setLoans] = useState<FeaturedLoan[]>([]);
  const [cards, setCards] = useState<FeaturedCard[]>([]);
  const [deposits, setDeposits] = useState<FeaturedDeposit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);

  useEffect(() => {
    fetchProducts();
  }, [activeTab]);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);

      if (activeTab === 'loans') {
        const { data, error } = await supabaseAdmin
          .from('featured_loans')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        setLoans(data || []);
      } else if (activeTab === 'cards') {
        const { data, error } = await supabaseAdmin
          .from('featured_cards')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        setCards(data || []);
      } else if (activeTab === 'deposits') {
        const { data, error } = await supabaseAdmin
          .from('featured_deposits')
          .select('*')
          .order('display_order', { ascending: true});

        if (error) throw error;
        setDeposits(data || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setErrorMessage('Ürünler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean, table: string) => {
    try {
      const { error } = await supabaseAdmin
        .from(table)
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      await fetchProducts();
      setSuccessMessage('Durum başarıyla güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error toggling status:', error);
      setErrorMessage('Durum güncellenirken hata oluştu');
    }
  };

  const toggleFeatured = async (id: string, currentStatus: boolean, table: string) => {
    try {
      const { error } = await supabaseAdmin
        .from(table)
        .update({ is_featured: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      await fetchProducts();
      setSuccessMessage('Öne çıkarma durumu güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error toggling featured:', error);
      setErrorMessage('Öne çıkarma durumu güncellenirken hata oluştu');
    }
  };

  const deleteProduct = async (id: string, table: string, productName: string) => {
    if (!confirm(`"${productName}" ürününü silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from(table)
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchProducts();
      setSuccessMessage('Ürün başarıyla silindi');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting product:', error);
      setErrorMessage('Ürün silinirken hata oluştu');
    }
  };

  const openEditModal = (product: any, type: 'loan' | 'card' | 'deposit') => {
    setEditingProduct({ ...product, type });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingProduct(null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;

    try {
      const table = editingProduct.type === 'loan' ? 'featured_loans'
        : editingProduct.type === 'card' ? 'featured_cards'
        : 'featured_deposits';

      const { type, ...productData } = editingProduct;

      const { error } = await supabaseAdmin
        .from(table)
        .update({
          ...productData,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingProduct.id);

      if (error) throw error;

      await fetchProducts();
      setSuccessMessage('Ürün başarıyla güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
      closeEditModal();
    } catch (error) {
      console.error('Error updating product:', error);
      setErrorMessage('Ürün güncellenirken hata oluştu');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setEditingProduct((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const getLoanTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'ihtiyac-kredisi': 'İhtiyaç Kredisi',
      'konut-kredisi': 'Konut Kredisi',
      'tasit-kredisi': 'Taşıt Kredisi',
      'kobi-kredisi': 'KOBİ Kredisi'
    };
    return types[type] || type;
  };

  const getDepositTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'tl_mevduat': 'TL Mevduat',
      'usd_mevduat': 'USD Mevduat',
      'eur_mevduat': 'EUR Mevduat'
    };
    return types[type] || type;
  };

  const filteredLoans = loans.filter(loan =>
    loan.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loan.bank_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCards = cards.filter(card =>
    card.card_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.bank_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDeposits = deposits.filter(deposit =>
    deposit.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deposit.bank_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Ürün Yönetimi</h1>
            <p className="text-slate-600">Kredi, kredi kartı ve mevduat ürünlerini yönetin</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors">
            <Plus size={20} />
            <span>Yeni Ürün Ekle</span>
          </button>
        </div>

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
            <Check size={18} />
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
            <AlertCircle size={18} />
            {errorMessage}
            <button
              onClick={() => setErrorMessage(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <X size={18} />
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200">
            <div className="flex items-center justify-between p-4">
              <div className="flex border border-slate-200 rounded-lg p-1 bg-slate-50">
                <button
                  onClick={() => setActiveTab('loans')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'loans'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <BanknoteIcon size={16} />
                  <span>Krediler</span>
                </button>
                <button
                  onClick={() => setActiveTab('cards')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'cards'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <CreditCardIcon size={16} />
                  <span>Kredi Kartları</span>
                </button>
                <button
                  onClick={() => setActiveTab('deposits')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'deposits'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Wallet size={16} />
                  <span>Mevduat</span>
                </button>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-64 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7BA45] mx-auto mb-4"></div>
                <p className="text-slate-600">Yükleniyor...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'loans' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Ürün</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Kredi Türü</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Faiz Oranı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Tutar Aralığı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Öne Çıkan</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Durum</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {filteredLoans.map((loan) => (
                        <tr key={loan.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <img src={loan.bank_logo} alt={loan.bank_name} className="h-8 w-auto object-contain" />
                              <div>
                                <div className="font-medium text-slate-900">{loan.product_name}</div>
                                <div className="text-sm text-slate-500">{loan.bank_name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-slate-900">{getLoanTypeLabel(loan.loan_type)}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-green-600">{loan.interest_rate}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-slate-900">{loan.min_amount} - {loan.max_amount}</div>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleFeatured(loan.id, loan.is_featured, 'featured_loans')}
                              className={`p-2 rounded-lg transition-colors ${
                                loan.is_featured
                                  ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                              }`}
                            >
                              {loan.is_featured ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleActive(loan.id, loan.is_active, 'featured_loans')}
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                loan.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-slate-100 text-slate-800'
                              }`}
                            >
                              {loan.is_active ? 'Aktif' : 'Pasif'}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditModal(loan, 'loan')}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Düzenle"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => deleteProduct(loan.id, 'featured_loans', loan.product_name)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Sil"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredLoans.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <BanknoteIcon size={48} className="mx-auto mb-2 text-slate-300" />
                      <p>Henüz kredi ürünü eklenmemiş</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'cards' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Kart</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Yıllık Aidat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Cashback</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Öne Çıkan</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Durum</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {filteredCards.map((card) => (
                        <tr key={card.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <img src={card.bank_logo} alt={card.bank_name} className="h-8 w-auto object-contain" />
                              <div>
                                <div className="font-medium text-slate-900">{card.card_name}</div>
                                <div className="text-sm text-slate-500">{card.bank_name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-slate-900">{card.annual_fee}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-green-600">{card.cashback || '-'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleFeatured(card.id, card.is_featured, 'featured_cards')}
                              className={`p-2 rounded-lg transition-colors ${
                                card.is_featured
                                  ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                              }`}
                            >
                              {card.is_featured ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleActive(card.id, card.is_active, 'featured_cards')}
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                card.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-slate-100 text-slate-800'
                              }`}
                            >
                              {card.is_active ? 'Aktif' : 'Pasif'}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditModal(card, 'card')}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Düzenle"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => deleteProduct(card.id, 'featured_cards', card.card_name)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Sil"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredCards.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <CreditCardIcon size={48} className="mx-auto mb-2 text-slate-300" />
                      <p>Henüz kredi kartı ürünü eklenmemiş</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'deposits' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Ürün</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Mevduat Türü</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Faiz Oranı</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Min. Tutar</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Öne Çıkan</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Durum</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {filteredDeposits.map((deposit) => (
                        <tr key={deposit.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <img src={deposit.bank_logo} alt={deposit.bank_name} className="h-8 w-auto object-contain" />
                              <div>
                                <div className="font-medium text-slate-900">{deposit.product_name}</div>
                                <div className="text-sm text-slate-500">{deposit.bank_name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-slate-900">{getDepositTypeLabel(deposit.deposit_type)}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-green-600">{deposit.interest_rate}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-slate-900">{deposit.min_amount}</span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleFeatured(deposit.id, deposit.is_featured, 'featured_deposits')}
                              className={`p-2 rounded-lg transition-colors ${
                                deposit.is_featured
                                  ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                              }`}
                            >
                              {deposit.is_featured ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => toggleActive(deposit.id, deposit.is_active, 'featured_deposits')}
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                deposit.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-slate-100 text-slate-800'
                              }`}
                            >
                              {deposit.is_active ? 'Aktif' : 'Pasif'}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditModal(deposit, 'deposit')}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Düzenle"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => deleteProduct(deposit.id, 'featured_deposits', deposit.product_name)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Sil"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredDeposits.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Wallet size={48} className="mx-auto mb-2 text-slate-300" />
                      <p>Henüz mevduat ürünü eklenmemiş</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {editModalOpen && editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">
                {editingProduct.type === 'loan' ? 'Kredi Düzenle'
                  : editingProduct.type === 'card' ? 'Kredi Kartı Düzenle'
                  : 'Mevduat Düzenle'}
              </h2>
              <button
                onClick={closeEditModal}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              {editingProduct.type === 'loan' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Banka Adı</label>
                      <input
                        type="text"
                        value={editingProduct.bank_name || ''}
                        onChange={(e) => handleInputChange('bank_name', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Ürün Adı</label>
                      <input
                        type="text"
                        value={editingProduct.product_name || ''}
                        onChange={(e) => handleInputChange('product_name', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Kredi Türü</label>
                    <select
                      value={editingProduct.loan_type || ''}
                      onChange={(e) => handleInputChange('loan_type', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    >
                      <option value="ihtiyac-kredisi">İhtiyaç Kredisi</option>
                      <option value="konut-kredisi">Konut Kredisi</option>
                      <option value="tasit-kredisi">Taşıt Kredisi</option>
                      <option value="kobi-kredisi">KOBİ Kredisi</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Açıklama</label>
                    <textarea
                      value={editingProduct.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      rows={2}
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Kredi Detayları</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Kredi Tutarı
                          <span className="text-slate-500 text-xs ml-1">(örn: 50.000 TL)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.min_amount || ''}
                          onChange={(e) => handleInputChange('min_amount', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="50.000 TL"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Vade
                          <span className="text-slate-500 text-xs ml-1">(örn: 36 Ay)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.min_term || ''}
                          onChange={(e) => handleInputChange('min_term', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="36 Ay"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Faiz Oranı
                          <span className="text-slate-500 text-xs ml-1">(örn: %2,99)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.interest_rate || ''}
                          onChange={(e) => handleInputChange('interest_rate', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="%2,99"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Toplam Tutar
                          <span className="text-slate-500 text-xs ml-1">(örn: 58.500 TL)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.max_amount || ''}
                          onChange={(e) => handleInputChange('max_amount', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="58.500 TL"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Kampanya Bilgileri (Opsiyonel)</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Kampanya Başlığı</label>
                        <input
                          type="text"
                          value={editingProduct.campaign_title || ''}
                          onChange={(e) => handleInputChange('campaign_title', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="Yeni Müşterilere Özel"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Kampanya Bitiş Tarihi</label>
                        <input
                          type="date"
                          value={editingProduct.campaign_end_date || ''}
                          onChange={(e) => handleInputChange('campaign_end_date', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Kampanya Açıklaması</label>
                      <textarea
                        value={editingProduct.campaign_description || ''}
                        onChange={(e) => handleInputChange('campaign_description', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        rows={2}
                        placeholder="Kampanya detaylarını buraya yazın..."
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Başvuru URL</label>
                    <input
                      type="url"
                      value={editingProduct.redirect_url || ''}
                      onChange={(e) => handleInputChange('redirect_url', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      placeholder="https://..."
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Banka Logosu URL</label>
                    <input
                      type="url"
                      value={editingProduct.bank_logo || ''}
                      onChange={(e) => handleInputChange('bank_logo', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      placeholder="https://..."
                    />
                    {editingProduct.bank_logo && (
                      <img
                        src={editingProduct.bank_logo}
                        alt="Logo Preview"
                        className="mt-2 h-12 object-contain"
                      />
                    )}
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Sıralama</label>
                    <input
                      type="number"
                      value={editingProduct.display_order || 0}
                      onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      min="0"
                    />
                  </div>
                </>
              )}

              {editingProduct.type === 'card' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Kart Adı</label>
                    <input
                      type="text"
                      value={editingProduct.card_name || ''}
                      onChange={(e) => handleInputChange('card_name', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Banka Adı</label>
                    <input
                      type="text"
                      value={editingProduct.bank_name || ''}
                      onChange={(e) => handleInputChange('bank_name', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Açıklama</label>
                    <textarea
                      value={editingProduct.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      rows={3}
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Kart Bilgileri</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Yıllık Ücret
                          <span className="text-slate-500 text-xs ml-1">(örn: 945 ₺)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.annual_fee || ''}
                          onChange={(e) => handleInputChange('annual_fee', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="945 ₺"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Puan/Mil Kazanımı
                          <span className="text-slate-500 text-xs ml-1">(örn: %1.3)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.cashback || ''}
                          onChange={(e) => handleInputChange('cashback', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="%1.3"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Hoş Geldin Bonusu
                          <span className="text-slate-500 text-xs ml-1">(örn: 7500 Mil)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.welcome_bonus || ''}
                          onChange={(e) => handleInputChange('welcome_bonus', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="7500 Mil"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Ek Kart Ücreti
                          <span className="text-slate-500 text-xs ml-1">(örn: 472 ₺)</span>
                        </label>
                        <input
                          type="text"
                          value={editingProduct.additional_card_fee || ''}
                          onChange={(e) => handleInputChange('additional_card_fee', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="472 ₺"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Kampanya Bilgileri (Opsiyonel)</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Kampanya Başlığı</label>
                        <input
                          type="text"
                          value={editingProduct.campaign_title || ''}
                          onChange={(e) => handleInputChange('campaign_title', e.target.value)}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          placeholder="Hoş Geldin Kampanyası"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Kampanya Açıklaması</label>
                      <textarea
                        value={editingProduct.campaign_description || ''}
                        onChange={(e) => handleInputChange('campaign_description', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        rows={2}
                        placeholder="İlk ay 10.000 TL harcamaya 5.000 mil"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Banka Logosu URL</label>
                    <input
                      type="url"
                      value={editingProduct.bank_logo || ''}
                      onChange={(e) => handleInputChange('bank_logo', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      placeholder="https://..."
                    />
                    {editingProduct.bank_logo && (
                      <img
                        src={editingProduct.bank_logo}
                        alt="Logo Preview"
                        className="mt-2 h-12 object-contain"
                      />
                    )}
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Yönlendirme URL</label>
                    <input
                      type="url"
                      value={editingProduct.redirect_url || ''}
                      onChange={(e) => handleInputChange('redirect_url', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      placeholder="https://..."
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Sıralama</label>
                    <input
                      type="number"
                      value={editingProduct.display_order || 0}
                      onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      min="0"
                    />
                  </div>
                </>
              )}

              {editingProduct.type === 'deposit' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Ürün Adı</label>
                    <input
                      type="text"
                      value={editingProduct.product_name || ''}
                      onChange={(e) => handleInputChange('product_name', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Açıklama</label>
                    <textarea
                      value={editingProduct.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Faiz Oranı</label>
                      <input
                        type="text"
                        value={editingProduct.interest_rate || ''}
                        onChange={(e) => handleInputChange('interest_rate', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Mevduat Türü</label>
                      <select
                        value={editingProduct.deposit_type || ''}
                        onChange={(e) => handleInputChange('deposit_type', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      >
                        <option value="tl_mevduat">TL Mevduat</option>
                        <option value="usd_mevduat">USD Mevduat</option>
                        <option value="eur_mevduat">EUR Mevduat</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Min. Tutar</label>
                      <input
                        type="text"
                        value={editingProduct.min_amount || ''}
                        onChange={(e) => handleInputChange('min_amount', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Min. Vade (Gün)</label>
                      <input
                        type="text"
                        value={editingProduct.min_term || ''}
                        onChange={(e) => handleInputChange('min_term', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Yönlendirme URL</label>
                    <input
                      type="url"
                      value={editingProduct.redirect_url || ''}
                      onChange={(e) => handleInputChange('redirect_url', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    />
                  </div>
                </>
              )}

              <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-6 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors font-medium"
                >
                  <Save size={18} />
                  <span>Kaydet</span>
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
